import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "./Sidebar.css";
import { api } from "../config/api";
import { getToken } from "../utils/auth";
import SendNotification from "./SendNotification";

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lotId } = useParams<{ lotId: string }>();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [displayLotId, setDisplayLotId] = useState<string>("");
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Check for mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Format the lot ID
  const formatLotId = (rawId: string): string => {
    const prefix = "PWP-PL-";
    let id = rawId;
    if (rawId.startsWith(prefix)) {
      id = rawId.substring(prefix.length);
    }
    const last8 = id.slice(-8);
    if (last8.length === 8) {
      return `${last8.slice(0, 4)}-${last8.slice(4)}`;
    }
    return id;
  };

  // Determine active page
  const getActivePage = (): string | null => {
    if (location.pathname.includes("device-manager")) return "device-manager";
    if (location.pathname.includes("vehicle-log")) return "vehicle-log";
    if (location.pathname.includes("session-log")) return "session-log";
    if (location.pathname.includes("disputes")) return "disputes";
    if (location.pathname.includes("customer")) return "customer";
    return null;
  };

  const activePage = getActivePage();

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Fetch lot data
  React.useEffect(() => {
    const fetchLotData = async () => {
      try {
        const response = await api.get('/parkinglots/get-all-parking-lots');
        const data = response.data;
        if (data && data.data) {
          const allLots = data.data;
          const foundLot = allLots.find((lot: any) => lot.lotId === lotId);
          if (foundLot) {
            setDisplayLotId(foundLot.displayLotId || foundLot.lotId);
          } else {
            setDisplayLotId(lotId || "");
          }
        }
      } catch (error) {
        console.error('Error fetching lot data:', error);
        setDisplayLotId(lotId || "");
      }
    };

    if (lotId) {
      fetchLotData();
    }
  }, [lotId]);

  return (
    <>
      {/* Toggle button that appears when sidebar is collapsed */}
      {isCollapsed && !isMobile && (
        <div className="sidebar-toggle-button collapsed" onClick={toggleSidebar}>
          <img src="/assets/RightArrow.svg" alt="Expand Sidebar" />
        </div>
      )}

      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Toggle button at the top of the sidebar */}
        {!isMobile && (
          <div className="sidebar-toggle-button" onClick={toggleSidebar}>
            <img src="/assets/LeftArrow.svg" alt="Collapse Sidebar" />
          </div>
        )}

        <div className="logo">
          <img
            src={isMobile ? "/assets/LogotypeSuperAdminHorizontal.svg" : "/assets/LogotypeSuperadmin.svg"}
            alt="Parallel Superadmin Logo"
            style={{ maxHeight: isMobile ? '70px' : 'auto' }}
          />
        </div>

        <div className="device-id">
          {displayLotId ? formatLotId(displayLotId) : (lotId ? formatLotId(lotId) : "")}
        </div>

        {!isMobile && (
          <div className="back-button" onClick={() => navigate("/dashboard")}>
            <img src="/assets/BackArrow.svg" alt="Back Arrow" />
            <span>Back</span>
          </div>
        )}

        {isMobile && (
          <div className={`hamburger-menu ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}

        <ul className={`menu ${isMenuOpen ? 'active' : ''}`}>
          {isMobile && (
            <li className="menu-section" onClick={() => {
              navigate("/dashboard");
              if (isMobile) setIsMenuOpen(false);
            }}>
              <button>Return to Dashboard</button>
            </li>
          )}
          <li
            className={activePage === "device-manager" ? "active" : ""}
            onClick={() => {
              navigate(`/lot/${lotId}/device-manager`);
              if (isMobile) setIsMenuOpen(false);
            }}
          >
            <button>Device Manager</button>
          </li>
          <li
            className={activePage === "vehicle-log" ? "active" : ""}
            onClick={() => {
              navigate(`/lot/${lotId}/vehicle-log`);
              if (isMobile) setIsMenuOpen(false);
            }}
          >
            <button>Vehicle Log</button>
          </li>
          <li
            className={activePage === "session-log" ? "active" : ""}
            onClick={() => {
              navigate(`/lot/${lotId}/session-log`);
              if (isMobile) setIsMenuOpen(false);
            }}
          >
            <button>Session Log</button>
          </li>
          <li
            className={activePage === "disputes" ? "active" : ""}
            onClick={() => {
              navigate(`/lot/${lotId}/disputes`);
              if (isMobile) setIsMenuOpen(false);
            }}
          >
            <button>Disputes</button>
          </li>
          <li
            className={activePage === "customer" ? "active" : ""}
            onClick={() => {
              navigate(`/lot/${lotId}/customer`);
              if (isMobile) setIsMenuOpen(false);
            }}
          >
            <button>Customer</button>
          </li>
          {/* Divider for footer actions */}
          {isMobile && <li className="menu-divider" aria-hidden="true"></li>}
          {isMobile && (
            <>
              <li onClick={() => {
                const token = getToken();
                if (token && lotId) {
                  const encodedToken = encodeURIComponent(token);
                  sessionStorage.setItem('superadmin_token_transfer', token);
                  window.open(
                    `https://operator.parkwithparallel.com/lot/${lotId}/dashboard/revenue?superadmin=true&token=${encodedToken}`,
                    "_blank"
                  );
                  if (isMobile) setIsMenuOpen(false);
                } else {
                  alert("Your session has expired or lot ID is missing.");
                }
              }}>
                <button>🡥 Operator Portal</button>
              </li>
              <li onClick={() => {
                setIsNotificationModalOpen(true);
                if (isMobile) setIsMenuOpen(false);
              }}>
                <button>Send Notification</button>
              </li>
            </>
          )}
        </ul>
        {/* Remove the .footer for mobile, keep only for desktop */}
        {!isMobile && (
          <div className="footer">
            <button
              className="link-button"
              onClick={() => {
                const token = getToken();
                if (token && lotId) {
                  const encodedToken = encodeURIComponent(token);
                  sessionStorage.setItem('superadmin_token_transfer', token);
                  window.open(
                    `https://operator.parkwithparallel.com/lot/${lotId}/dashboard/revenue?superadmin=true&token=${encodedToken}`,
                    "_blank"
                  );
                } else {
                  alert("Your session has expired or lot ID is missing.");
                }
              }}
            >
              Operator Portal 🡥
            </button>
            <button
              className="link-button"
              onClick={() => {
                setIsNotificationModalOpen(true);
              }}
            >
              Send Notification
            </button>
          </div>
        )}
      </div>

      <SendNotification
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
      />
    </>
  );
};

export default Sidebar;
